import { useState } from 'react'
import Papa from 'papaparse'
import { detectCsvEncoding } from '../../utils/csvEncoding'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePlayers } from '../../hooks/usePlayers'
import { useTeams } from '../../hooks/useTeams'
import { supabase } from '../../lib/supabase'
import { Card, Button, Input, Modal, DarkModeToggleCompact } from '../../components/ui'
import { UserCircle, Plus, Trash2, ArrowLeft, Edit2, Download, FileUp, Upload, Sparkles, AlertTriangle } from 'lucide-react'

export const PlayersPage = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { tournamentId } = useParams()
    const { players, loading, createPlayer, updatePlayer, deletePlayer } = usePlayers(tournamentId)
    const { teams } = useTeams(tournamentId)
    const [showModal, setShowModal] = useState(false)
    const [editingPlayer, setEditingPlayer] = useState(null)
    const [newPlayer, setNewPlayer] = useState({ name: '', team_id: '' })
    const [uploading, setUploading] = useState(false)
    const [sportType, setSportType] = useState('goals')

    // Fetch sport type to match premium styling
    useState(() => {
        const getSport = async () => {
            const { data } = await supabase.from('tournaments').select('sport_type').eq('id', tournamentId).single()
            if (data) setSportType(data.sport_type || 'goals')
        }
        getSport()
    }, [tournamentId])

    const handleCreate = async (e) => {
        e.preventDefault()
        try {
            await createPlayer(newPlayer)
            setShowModal(false)
            setNewPlayer({ name: '', team_id: '' })
        } catch (err) {
            alert(t('common.save_error') + err.message)
        }
    }

    const handleUpdate = async (e) => {
        e.preventDefault()
        try {
            await updatePlayer(editingPlayer.id, {
                name: editingPlayer.name,
                team_id: editingPlayer.team_id
            })
            setEditingPlayer(null)
        } catch (err) {
            alert(t('common.save_error') + err.message)
        }
    }

    const handleDelete = async (playerId) => {
        if (!confirm(t('common.confirm_delete_player'))) return
        try {
            await deletePlayer(playerId)
        } catch (err) {
            alert(t('common.save_error') + err.message)
        }
    }

    const downloadTemplate = () => {
        const headers = ['Naam', 'Team']
        const csvContent = headers.join(',')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', 'players_import_template.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const downloadCSV = () => {
        if (!players || players.length === 0) {
            alert('Geen spelers om te exporteren.')
            return
        }
        const headers = ['Naam', 'Team']
        const csvContent = [
            headers.join(','),
            ...players.map(p => `"${p.name}","${p.team?.name || ''}"`)
        ].join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `players_export_${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleFileUpload = async (event) => {
        const file = event.target.files[0]
        if (!file) return

        const encoding = await detectCsvEncoding(file)

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            encoding: encoding,
            complete: async (results) => {
                const data = results.data
                if (!data || data.length === 0) {
                    alert('Het bestand is leeg of kon niet worden gelezen.')
                    return
                }

                setUploading(true)
                let successCount = 0
                let errors = []

                let teamMap = {}
                if (teams) {
                    teams.forEach(t => teamMap[t.name.toLowerCase()] = t.id)
                }

                for (let i = 0; i < data.length; i++) {
                    const row = data[i]
                    const nameKey = Object.keys(row).find(k => k.toLowerCase() === 'naam' || k.toLowerCase() === 'name' || k.toLowerCase() === 'speler')
                    const teamKey = Object.keys(row).find(k => k.toLowerCase() === 'team' || k.toLowerCase() === 'club')

                    const name = nameKey ? row[nameKey]?.trim() : null
                    const teamName = teamKey ? row[teamKey]?.trim() : null

                    if (name) {
                        let teamId = null
                        if (teamName && teamName !== '') {
                            const normTeamName = teamName.toLowerCase()
                            if (teamMap[normTeamName]) {
                                teamId = teamMap[normTeamName]
                            } else {
                                try {
                                    const { data: newTeam, error: teamError } = await supabase.from('teams').insert({
                                        tournament_id: tournamentId,
                                        name: teamName
                                    }).select().single()
                                    if (teamError) throw teamError
                                    teamId = newTeam.id
                                    teamMap[normTeamName] = newTeam.id
                                } catch (err) {
                                    console.error("Error creating team on the fly:", err)
                                    errors.push(`Regel ${i + 2}: Kon team '${teamName}' niet aanmaken.`)
                                }
                            }
                        }

                        try {
                            await createPlayer({ name, team_id: teamId })
                            successCount++
                        } catch (err) {
                            console.error(`Error importing player at line ${i + 2}:`, err)
                            errors.push(`Regel ${i + 2}: ${name} - ${err.message}`)
                        }
                    } else {
                        errors.push(`Regel ${i + 2}: Geen spelersnaam gevonden.`)
                    }
                }

                setUploading(false)
                alert(t('teams_page.import_success', { count: successCount }) + (errors.length > 0 ? `\n\n${t('common.errors')}:\n${errors.join('\n')}` : ''))
                if (successCount > 0) window.location.reload()
                event.target.value = ''
            },
            error: (err) => {
                alert('Fout tijdens het parsen van CSV: ' + err.message)
            }
        })
    }

    const handleDeleteAll = async () => {
        if (players.length === 0) return
        if (!confirm('Weet je zeker dat je alle spelers wilt verwijderen?')) return
        const check = prompt('Typ VERWIJDEREN om te bevestigen:')
        if (check !== 'VERWIJDEREN' && check !== 'DELETE') return

        try {
            const { error } = await supabase.from('players').delete().eq('tournament_id', tournamentId)
            if (error) throw error
            alert('Alle spelers succesvol verwijderd.')
            window.location.reload()
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }

    const isEsports = sportType === 'esports'

    return (
        <div className="min-h-screen">
            <div className="premium-container animate-fadeIn">
                {/* Hero Section */}
                <div className="mb-8 animate-slideUp">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate(`/admin/tournaments/${tournamentId}/teams`)} 
                        className={`mb-4 ${isEsports ? 'text-slate-300 hover:text-white hover:bg-white/5' : ''}`}
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        {t('common.back')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className={`p-3.5 rounded-2xl shadow-lg transition-all duration-500 hover:rotate-12 ${
                                isEsports 
                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' 
                                    : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_0_20px_rgba(79,70,229,0.3)]'
                            }`}>
                                <UserCircle className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className={`text-5xl font-bold leading-tight ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    {t('common.players')}
                                </h1>
                                <p className={`text-lg mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>
                                    {t('teams_page.subtitle')}
                                </p>
                            </div>
                        </div>

                        <DarkModeToggleCompact />
                    </div>

                    {/* Premium Action Toolbar */}
                    <div className={`flex flex-wrap gap-4 items-center p-5 rounded-2xl border transition-all duration-300 ${
                        isEsports 
                            ? 'bg-purple-950/10 border-purple-500/20 shadow-[0_0_25px_rgba(168,85,247,0.04)]' 
                            : 'bg-white border-gray-100 shadow-md'
                    }`}>
                        <div className="flex flex-wrap gap-2 pr-3 border-r border-gray-200/10">
                            {players.length > 0 && (
                                <Button 
                                    variant="ghost" 
                                    onClick={handleDeleteAll} 
                                    size="sm" 
                                    className="text-red-500 hover:bg-red-500/10 rounded-xl"
                                >
                                    <Trash2 size={16} />
                                </Button>
                            )}
                            <Button 
                                variant="ghost" 
                                onClick={downloadTemplate} 
                                size="sm" 
                                className="rounded-xl font-semibold"
                            >
                                <Download size={16} className="mr-2" /> Template
                            </Button>
                            <label className="cursor-pointer">
                                <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
                                <div className={`inline-flex items-center justify-center px-4 py-2 text-sm font-semibold hover:bg-white/5 rounded-xl transition-all cursor-pointer ${
                                    isEsports ? 'text-purple-300' : 'text-gray-700 hover:bg-gray-100'
                                }`}>
                                    <FileUp size={16} className="mr-2" /> Import
                                </div>
                            </label>
                            <Button 
                                variant="ghost" 
                                onClick={downloadCSV} 
                                size="sm" 
                                className="rounded-xl font-semibold"
                            >
                                <Upload size={16} className="mr-2 rotate-180" /> Export
                            </Button>
                        </div>

                        <div className="flex-1"></div>

                        <Button 
                            onClick={() => setShowModal(true)} 
                            variant="primary" 
                            className={`py-3.5 px-6 rounded-xl font-bold flex items-center gap-2 ${
                                isEsports ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]' : ''
                            }`}
                        >
                            <Plus size={20} /> Toevoegen
                        </Button>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className={`rounded-3xl border shadow-xl p-6 sm:p-8 ${
                    isEsports ? 'bg-purple-950/10 border-purple-500/20 text-white' : 'bg-white border-gray-100'
                }`}>
                    {loading || uploading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
                            {uploading && <p className="text-gray-500 animate-pulse font-medium">Bezig met uploaden...</p>}
                        </div>
                    ) : players.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                                <UserCircle className="text-white/20 animate-pulse" size={40} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-500 mb-2">Geen spelers gevonden</h3>
                            <p className="text-sm text-gray-400 max-w-sm mx-auto">
                                Voeg handmatig spelers toe of importeer een CSV-lijst om te beginnen.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {players.map(player => (
                                <div 
                                    key={player.id} 
                                    className={`group relative overflow-hidden rounded-2xl border p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${
                                        isEsports 
                                            ? 'bg-purple-950/5 border-purple-500/15 hover:border-purple-500/30' 
                                            : 'bg-gray-50 border-gray-150 hover:bg-white hover:border-gray-250'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className={`p-3 rounded-xl shadow-md ${
                                                isEsports ? 'bg-purple-500/20 text-purple-300' : 'bg-indigo-100 text-indigo-600'
                                            }`}>
                                                <UserCircle size={22} />
                                            </div>
                                            <div>
                                                <h3 className={`text-lg font-bold ${isEsports ? 'text-white' : 'text-gray-900'}`}>{player.name}</h3>
                                                <p className={`text-xs mt-0.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    {player.team?.name || t('common.no_team')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <Button
                                                variant="secondary"
                                                onClick={() => setEditingPlayer(player)}
                                                size="sm"
                                                className="p-2.5 rounded-xl border border-gray-200/10"
                                            >
                                                <Edit2 size={14} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                onClick={() => handleDelete(player.id)}
                                                size="sm"
                                                className="p-2.5 rounded-xl text-red-500 hover:bg-red-500/10"
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-purple-500 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('common.player') + ' ' + t('common.save').toLowerCase()}>
                <form onSubmit={handleCreate} className="space-y-6">
                    <Input
                        label={t('common.player') + ' ' + t('teams_page.input_name_label').split(' ')[1]}
                        value={newPlayer.name}
                        onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                        placeholder="bijv. John Doe"
                        required
                    />
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">{t('common.team')}</label>
                        <select
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                            value={newPlayer.team_id}
                            onChange={(e) => setNewPlayer({ ...newPlayer, team_id: e.target.value })}
                        >
                            <option value="">{t('teams_page.select_poule_placeholder')}</option>
                            {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                        </select>
                    </div>
                    <Button type="submit" variant="primary" className="w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2">
                        <Plus size={20} />
                        {t('common.save')}
                    </Button>
                </form>
            </Modal>

            {/* Edit Modal */}
            <Modal isOpen={!!editingPlayer} onClose={() => setEditingPlayer(null)} title={t('teams_page.modal_edit_title')}>
                <form onSubmit={handleUpdate} className="space-y-6">
                    <Input
                        label={t('common.player') + ' ' + t('teams_page.input_name_label').split(' ')[1]}
                        value={editingPlayer?.name || ''}
                        onChange={(e) => setEditingPlayer({ ...editingPlayer, name: e.target.value })}
                        placeholder="bijv. John Doe"
                        required
                    />
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">{t('common.team')}</label>
                        <select
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                            value={editingPlayer?.team_id || ''}
                            onChange={(e) => setEditingPlayer({ ...editingPlayer, team_id: e.target.value })}
                        >
                            <option value="">{t('common.no_team')}</option>
                            {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                        </select>
                    </div>
                    <div className="flex gap-3">
                        <Button type="submit" variant="primary" className="flex-1 py-3.5 rounded-xl font-bold">{t('common.save')}</Button>
                        <Button type="button" variant="ghost" onClick={() => setEditingPlayer(null)} className="flex-1 py-3.5 rounded-xl font-bold">{t('common.cancel')}</Button>
                    </div>
                </form>
            </Modal>
        </div>
    )
}
